import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper, InputAdornment, MenuItem, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from '../Commissioning/utils';
import './CableInsulationMeasurements.scss';


// Measurement fields and the unit adornment each carries (empty string = no unit).
const BASE_MEASUREMENT_FIELDS = [
  'cableResistance', 'srCableLength', 'srCapacitance',
  'meggerInsulationResistance1', 'meggerTestVoltage1',
  'meggerInsulationResistance2', 'meggerTestVoltage2',
  'meggerInsulationResistance3', 'meggerTestVoltage3'
];


const MEASUREMENT_UNITS: Record<string, string> = {
  cableResistance: 'Ω',
  srCapacitance: 'nF',
  meggerTestVoltage: 'VDC',
  meggerInsulationResistance: 'Ω'
};
// removes any trailing numbers from the end of a field name e.g meggerTestVoltage1 -> meggerTestVoltage
const cleanedField = (field: string) => MEASUREMENT_UNITS[field.replace(/\d+$/, '')];

const MEGGER_TEST_VOLTAGE_OPTIONS = ['500', '1000', '2500'];

export default function CableInsulationMeasurements (props) {
  const { i18nPrefix, data, onChange, readOnly, isSubCircuit, subCircuitIdx, autoFocusTag, onDelete } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const MEASUREMENT_FIELDS = [
    isSubCircuit ? 'subCircuitTag' :'heatTraceCableTag',
    ...BASE_MEASUREMENT_FIELDS
  ];

  // Where this instance's values live under `extras`: the main circuit writes to
  // measurements.<field>, a sub-circuit to measurements.subCircuits[idx].<field>.
  const measurementsPath = useMemo(
    () => (isSubCircuit ? ['measurements', 'subCircuits', subCircuitIdx] : ['measurements']),
    [isSubCircuit, subCircuitIdx],
  );

  const [measurements, setMeasurements] = useState(() => Map(data?.getIn(measurementsPath) || {}));

  useEffect(() => {
    setMeasurements(Map(data?.getIn(measurementsPath) || {}));
  }, [data, measurementsPath]);

  const onMeasurementChange = (key, val) => {
    setMeasurements(measurements.set(key, val));
    onChange(['extras', ...measurementsPath, key], val);
  };

  const renderMeasurementField = (field) => {
    const unit = cleanedField(field);
    if (field.startsWith('meggerTestVoltage')) {
      return (
        <TextField
          select
          fullWidth
          disabled={readOnly}
          value={measurements.get(field) || ''}
          onChange={(e) => onMeasurementChange(field, e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position='end' sx={{ marginRight: '20px' }}>{unit}</InputAdornment> }}
        >
          <MenuItem value=''>—</MenuItem>
          {MEGGER_TEST_VOLTAGE_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        fullWidth
        disabled={readOnly}
        // Only ever true for a block that was just added, so it fires on mount and never again.
        autoFocus={autoFocusTag && field === 'subCircuitTag'}
        value={measurements.get(field) || ''}
        onChange={(e) => onMeasurementChange(field, e.target.value)}
        InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
      />
    );
  };

  const measurementPairs = pairUp(MEASUREMENT_FIELDS);

  const renderPairedTable = (pairs, labelFor, renderField) => (
    <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pairs.map(([fieldA, fieldB]) => (
            <TableRow key={fieldA}>
              <TableCell className='sub-headers'>{t(labelFor(fieldA))}</TableCell>
              <TableCell className='body-cell'>{renderField(fieldA)}</TableCell>
              {fieldB ? (
                <>
                  <TableCell className='sub-headers'>{t(labelFor(fieldB))}</TableCell>
                  <TableCell className='body-cell'>{renderField(fieldB)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // The title numbers the blocks for the technician, so it's 1-based; `subCircuitIdx` itself
  // stays 0-based because it indexes extras.measurements.subCircuits.
  const measurementTitle = isSubCircuit ? t(`${i18nPrefix}.measurements.subCircuit.title`, { subCircuitIdx: `${subCircuitIdx + 1}` }) : t(`${i18nPrefix}.measurements.title`);
  return (
    <div className='cable-insulation-measurements'>
      <Paper sx={{ marginBottom: '30px' }}>
        <div className='measurement-block-header'>
          <h3>{measurementTitle}</h3>
          {onDelete && (
            <IconButton
              className='measurement-block-delete'
              aria-label={t('common.delete')}
              disabled={readOnly}
              onClick={onDelete}
            >
              <DeleteIcon className='delete-icon-danger' />
            </IconButton>
          )}
        </div>
        {renderPairedTable(measurementPairs, (field) => `${i18nPrefix}.measurements.${field}`, renderMeasurementField)}
      </Paper>
    </div>
  );
}

CableInsulationMeasurements.propTypes = {
  i18nPrefix: PropTypes.string.isRequired,
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  isSubCircuit: PropTypes.bool,
  subCircuitIdx: PropTypes.number,
  autoFocusTag: PropTypes.bool,
  // Omitted for the main circuit, whose block can't be removed; passed for each sub-circuit.
  onDelete: PropTypes.func
};

CableInsulationMeasurements.defaultProps = {
  readOnly: false,
  isSubCircuit: false,
  subCircuitIdx: null,
  autoFocusTag: false,
  onDelete: null,
};
